import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, MessageSquare, ShoppingBag, Heart, CheckCircle2, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * ConsumerWalkthrough - Interactive onboarding overlay for first-time consumers
 * 
 * Walks users through the app with animated tooltips:
 * 1. Welcome - Introduction to DealShaq
 * 2. Food Assistant - How to use natural language search
 * 3. Browse Deals - Exploring available deals
 * 4. Favorites - Setting up preferences
 * 5. Notifications - Getting alerts for matching deals
 * 6. Success - Ready to save!
 */

const WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to DealShaq! 🎉',
    description: 'Discover sizzling hot deals on sizzling hot deals from local retailers. Save money while reducing food waste!',
    target: null,
    position: 'center',
    icon: Sparkles,
  },
  {
    id: 'food-assistant',
    title: 'Meet Your Food Assistant',
    description: 'Just tell me what you need in plain English! Try "I need dinner for 3 under $10" or "Show me Halal options". I\'ll find the best matching deals.',
    target: '[data-testid="food-assistant-input"]',
    position: 'bottom',
    icon: MessageSquare,
    highlight: true,
  },
  {
    id: 'example-queries',
    title: 'Try These Examples',
    description: 'Click any example to get started quickly. I understand budget constraints, dietary needs, meal types, and certifications like Halal and Kosher.',
    target: '[data-testid="example-query-0"]',
    position: 'bottom',
    icon: Sparkles,
    highlight: true,
  },
  {
    id: 'browse-deals',
    title: 'Browse All Deals',
    description: 'See all available deals from your local retailers. Filter by category, price, or certification to find exactly what you need.',
    target: '[data-testid="browse-deals-btn"]',
    position: 'bottom',
    icon: ShoppingBag,
    highlight: true,
  },
  {
    id: 'favorites',
    title: 'Set Your Favorites',
    description: 'Add your favorite categories and items. We\'ll notify you when matching deals become available!',
    target: '[data-testid="favorites-btn"]',
    position: 'bottom',
    icon: Heart,
    highlight: true,
  },
  {
    id: 'success',
    title: 'You\'re All Set! 🛒',
    description: 'Start exploring deals and saving money! Retailers post new surplus items daily, so check back often for fresh deals.',
    target: null,
    position: 'center',
    icon: PartyPopper,
  },
];

const ConsumerWalkthrough = ({ 
  isOpen, 
  onClose, 
  onComplete,
  startAtStep = 0 
}) => {
  const [currentStep, setCurrentStep] = useState(startAtStep);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetElement, setTargetElement] = useState(null);
  
  // Reset to first step whenever walkthrough is opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);
  
  const step = WALKTHROUGH_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WALKTHROUGH_STEPS.length - 1;
  const StepIcon = step.icon;
  
  // Position the tooltip relative to the target element
  const updateTooltipPosition = useCallback(() => {
    if (!step.target) {
      setTargetElement(null);
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    
    const element = document.querySelector(step.target);
    if (element) {
      setTargetElement(element);
      const rect = element.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
      
      let top, left;
      
      switch (step.position) {
        case 'top':
          top = rect.top + scrollTop - 150;
          left = rect.left + scrollLeft + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + scrollTop + 20;
          left = rect.left + scrollLeft + rect.width / 2;
          break;
        case 'left':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.left + scrollLeft - 350;
          break;
        case 'right':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.right + scrollLeft + 20;
          break;
        default:
          top = rect.bottom + scrollTop + 20;
          left = rect.left + scrollLeft + rect.width / 2;
      }
      
      setTooltipPosition({ 
        top: `${top}px`, 
        left: `${left}px`,
        transform: step.position === 'bottom' || step.position === 'top' 
          ? 'translateX(-50%)' 
          : 'translateY(-50%)'
      });
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetElement(null);
    }
  }, [step]);
  
  useEffect(() => {
    if (isOpen) {
      updateTooltipPosition();
      window.addEventListener('resize', updateTooltipPosition);
      window.addEventListener('scroll', updateTooltipPosition);
      
      return () => {
        window.removeEventListener('resize', updateTooltipPosition);
        window.removeEventListener('scroll', updateTooltipPosition);
      };
    }
  }, [isOpen, currentStep, updateTooltipPosition]);
  
  // Watch for target element to appear
  useEffect(() => {
    if (!isOpen || !step.target) return;
    
    const observer = new MutationObserver(() => {
      const element = document.querySelector(step.target);
      if (element) {
        updateTooltipPosition();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isOpen, step, updateTooltipPosition]);
  
  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleComplete = () => {
    localStorage.setItem('consumer_walkthrough_completed', 'true');
    localStorage.setItem('consumer_walkthrough_completed_at', new Date().toISOString());
    
    if (onComplete) onComplete();
    onClose();
  };
  
  const handleSkip = () => {
    localStorage.setItem('consumer_walkthrough_skipped', 'true');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] transition-opacity"
        onClick={handleSkip}
      />
      
      {/* Highlight ring around target element */}
      {targetElement && step.highlight && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetElement.getBoundingClientRect().top - 4 + 'px',
            left: targetElement.getBoundingClientRect().left - 4 + 'px',
            width: targetElement.offsetWidth + 8 + 'px',
            height: targetElement.offsetHeight + 8 + 'px',
            boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.6), 0 0 0 9999px rgba(0, 0, 0, 0.5)',
            borderRadius: '8px',
            animation: 'pulse-emerald 2s infinite',
          }}
        />
      )}
      
      {/* Tooltip card */}
      <Card 
        className="fixed z-[10000] w-[340px] shadow-2xl border-2 border-emerald-300 bg-white"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
        }}
        data-testid="consumer-walkthrough-tooltip"
      >
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-white/20 p-2 rounded-lg">
                  <StepIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-white/80 text-sm">
                  Step {currentStep + 1} of {WALKTHROUGH_STEPS.length}
                </span>
              </div>
              <button
                onClick={handleSkip}
                className="text-white/70 hover:text-white transition-colors"
                data-testid="consumer-walkthrough-skip"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-white font-bold text-lg mt-3">{step.title}</h3>
          </div>
          
          {/* Content */}
          <div className="p-4">
            <p className="text-gray-600 text-sm leading-relaxed">
              {step.description}
            </p>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 my-4">
              {WALKTHROUGH_STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-emerald-500 w-6' 
                      : idx < currentStep 
                        ? 'bg-emerald-400' 
                        : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            {/* Navigation buttons */}
            <div className="flex gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  className="flex-1"
                  data-testid="consumer-walkthrough-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                data-testid="consumer-walkthrough-next"
              >
                {isLastStep ? 'Start Saving!' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
            
            {/* Skip link */}
            {!isLastStep && (
              <button
                onClick={handleSkip}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3"
              >
                Skip tutorial
              </button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse-emerald {
          0%, 100% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.6), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.3), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
        }
      `}</style>
    </>
  );
};

export default ConsumerWalkthrough;

/**
 * Hook to manage walkthrough state
 */
export const useConsumerWalkthrough = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Check if user should see the walkthrough
  const shouldShowWalkthrough = useCallback(() => {
    const completed = localStorage.getItem('consumer_walkthrough_completed');
    const skipped = localStorage.getItem('consumer_walkthrough_skipped');
    
    return !completed && !skipped;
  }, []);
  
  const openWalkthrough = useCallback(() => setIsOpen(true), []);
  const closeWalkthrough = useCallback(() => setIsOpen(false), []);
  
  const resetWalkthrough = useCallback(() => {
    localStorage.removeItem('consumer_walkthrough_completed');
    localStorage.removeItem('consumer_walkthrough_completed_at');
    localStorage.removeItem('consumer_walkthrough_skipped');
  }, []);
  
  return {
    isOpen,
    openWalkthrough,
    closeWalkthrough,
    shouldShowWalkthrough,
    resetWalkthrough,
  };
};
